// カートの見積書（Issue #113 / #115）の店ごとの表。hook（src/hooks/cart-estimate.ts のダイアログ）と
// サーバー（src/server/cart-estimate/。入力の検証と印刷用の画面）で共有する。
// 旧 wuAdminsite `Views/Estimate/Print.cshtml`（支払い方法の出し分け・送付先・NP の注意）を**値も順番も変えずに**写したもの。
//
//   POST /api/cart-estimate/  本文 siteId・[i].productName・[i].unitPrice・[i].selectNum・name・payType・pref → 印刷用の画面（HTML）
//
// ★旧は店を `SiteInfoes.TargetSite`（enum）で見分けていたが、wuStorefront は SiteInfoes の Id・Name しか読まない。
//   なので SiteInfoId → TargetSite の対応をここに持つ（対象はカートに見積書ボタンを出す 11 店）。
// ★DOM に触らないこと（サーバーの tsconfig から読む）。

/** 旧の enum TargetSite のうち、この表で使う名前（旧の綴りのまま） */
export type TargetSite =
  | 'FukuroIn'
  | 'Anshinya'
  | 'Opp本舗'
  | 'Dansai'
  | 'PackJapan'
  | '関西激安'
  | 'マスク本舗'
  | '紙ストロー'
  | 'ビニール袋'
  | 'コミック侍'
  | 'Eco侍'
  | 'きらきらぷんぷん丸'
  | 'ボタンマート'
  | 'Lustia'

/** SiteInfoId → TargetSite。見積書を受ける店（shared/hooks.ts の CART_ESTIMATE_SITES と同じ 11 店） */
export const ESTIMATE_SITES: Readonly<Record<number, TargetSite>> = {
  1: 'FukuroIn',
  2: 'Anshinya',
  3: 'Opp本舗',
  4: 'Dansai',
  6: 'PackJapan',
  9: '紙ストロー',
  10: 'ビニール袋',
  11: 'コミック侍',
  14: 'きらきらぷんぷん丸',
  15: 'ボタンマート',
  16: 'Lustia',
}

/**
 * カートの「見積書発行」をダイアログ + wuStorefront の見積書の画面に切り替えた店（Issue #115）。
 * ここに無い店は旧の流れのまま（旧 OppSite の /Estimate/Print をクエリ付きで別タブに開く）。1 店ずつ足して移す。
 * ★足せるのは ESTIMATE_SITES にある店だけ（hooks/cart-estimate.test.ts が見張る）。
 */
export const ESTIMATE_DIALOG_SITES: readonly number[] = [
  1, // OPP袋のワークアップ fukuro.in（2026-11-03。同 2,038 件。見積書の 8〜9 割を占めるので最後に移した）
  2, // マスク製造（2026-10-27。直近 6 か月 9 件）
  3, // OPP本店（2026-10-27。同 24 件）
  4, // 台紙（2026-10-31。同 55 件）
  6, // パックジャパン（2026-10-31。同 133 件）
  9, // 紙ストロー（2026-10-25。見積書が少ない店から: 直近 6 か月 3 件）
  10, // 宅配ビニール袋（2026-10-27。同 13 件）
  11, // コミック侍（2026-10-29。同 44 件）
  14, // きらきらぷんぷん丸（2026-10-29。同 43 件）
  15, // ボタンマート（同 5 件）
  16, // Lustia（2026-10-25。最初の 1 店）
]

/** 送付先。旧の入力画面のラジオの値そのまま（マスク本舗は hidden で「その他」） */
export const PREFS = ['その他', '北海道', '沖縄'] as const
export type Pref = (typeof PREFS)[number]

/** 送付先のラジオの表示（旧 Print.cshtml） */
export const PREF_LABELS: Readonly<Record<Pref, string>> = {
  その他: '全国（北海道・沖縄除く）',
  北海道: '北海道',
  沖縄: '沖縄',
}

/**
 * 支払い方法の値と表示名（旧 Print.cshtml の option）。6 = ネコポスは旧でもコメントアウトされていて出ない。
 * 値は Estimates.PayType にそのまま入る。
 */
export const PAY_TYPES: readonly { value: number; label: string }[] = [
  { value: 1, label: '代金引換' },
  { value: 2, label: '銀行振込' },
  { value: 3, label: 'クレジットカード' },
  { value: 4, label: 'コンビニ後払い' },
  { value: 5, label: '請求書後払い' },
  { value: 7, label: 'AmazonPay' },
  { value: 8, label: 'PayPay' },
]

const creditTarget: TargetSite[] = ['FukuroIn', 'Anshinya', 'Opp本舗', 'PackJapan', 'Dansai', 'マスク本舗', '紙ストロー', 'ビニール袋', 'コミック侍', 'Eco侍', 'きらきらぷんぷん丸', 'ボタンマート']
const npTarget: TargetSite[] = ['FukuroIn', 'Anshinya', 'Opp本舗', 'Dansai', 'PackJapan', 'マスク本舗', '紙ストロー', 'ビニール袋', 'コミック侍', 'Eco侍', 'きらきらぷんぷん丸', 'ボタンマート']
const flexB2BTarget: TargetSite[] = ['FukuroIn', 'Anshinya', 'PackJapan', 'Dansai', '紙ストロー', 'ビニール袋', 'Eco侍']
const amazonPayTarget: TargetSite[] = ['FukuroIn', '紙ストロー', 'コミック侍', 'Eco侍', 'Dansai', 'PackJapan', 'Anshinya', 'ビニール袋', 'Opp本舗', 'マスク本舗', 'きらきらぷんぷん丸', 'ボタンマート']
const paypayTarget: TargetSite[] = ['FukuroIn', 'Anshinya', 'Opp本舗', 'PackJapan', 'Dansai', 'マスク本舗', '紙ストロー', 'ビニール袋', 'コミック侍', 'Eco侍', 'きらきらぷんぷん丸', 'ボタンマート']

/** 店ごとに出す支払い方法の値（旧 Print.cshtml の @if をそのまま並べたもの。順番も同じ） */
export function payTypesFor(site: TargetSite): number[] {
  const out: number[] = []
  if (site !== 'マスク本舗' && site !== 'きらきらぷんぷん丸' && site !== 'ボタンマート') out.push(1)
  if (site !== 'マスク本舗' && site !== 'コミック侍' && site !== 'きらきらぷんぷん丸' && site !== 'ボタンマート') out.push(2)
  if (creditTarget.includes(site)) out.push(3)
  if (npTarget.includes(site)) out.push(4)
  if (flexB2BTarget.includes(site)) out.push(5)
  if (amazonPayTarget.includes(site)) out.push(7)
  if (paypayTarget.includes(site)) out.push(8)
  return out
}

/** マスク本舗は送付先を選ばせない（hidden で「その他」） */
export const asksPref = (site: TargetSite): boolean => site !== 'マスク本舗'

/** コンビニ後払い（4）の上限。旧は合計（送料・手数料を足す前）が 49,999 円を超えると、4 を選んだときに発行ボタンを止める */
export const NP_LIMIT_OVER = 49999
export const NP_PAY_TYPE = 4
export const NP_WARNING = 'NP後払いでのご利用限度額(5万円)を超えている為、このお支払方法でのお見積書は発行できません。'

/** 宛名の上限（本番の 2026-04〜10 の最大は 51 文字。旧は上限なし） */
export const NAME_MAX = 200
/** カートの品物の数の上限（本番の 2026-04〜10 の最大は 11 品） */
export const ITEMS_MAX = 100
